import { Typography } from '@mui/material';
import { useQueryClient } from 'react-query';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmationModalProps } from '~/Components';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { ToastVariant, useToastMessage } from '~/Hooks';
import { IStaff } from '~/Models/DTO/Staff.model';
import { IMenuAction } from '~/Models/interfaces/menuAction.models';
import { selectErrors } from '~/store/app';
import { requestUpdateProvider } from '~/store/provider';

export const useStaffUpdateModal: ({
  setConfirmationModalProps,
}: {
  setConfirmationModalProps: React.Dispatch<React.SetStateAction<ConfirmationModalProps>>;
}) => (item: IStaff) => IMenuAction = ({ setConfirmationModalProps }) => {
  const { t } = useTranslation();
  const { showToast } = useToastMessage();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const providerUpdateError = useSelector(selectErrors(REQUEST_ACTIONS.UPDATE_PROVIDER));

  const updateProvider = (providerId: string, data: any) =>
    dispatch(requestUpdateProvider(providerId, data));

  useEffect(() => {
    if (providerUpdateError) {
      showToast('', extractErrorMessage(providerUpdateError), ToastVariant.ERROR, true);
    }
  }, [providerUpdateError, showToast]);

  const ModalContent = (staff: IStaff) => (
    <div>
      <Typography>
        <Trans
          i18nKey="staff.changeOwnerDescription"
          values={{ owner: `${staff.firstName} ${staff.lastName}` }}
          components={[<b key="bold" />]}
        />
      </Typography>
    </div>
  );

  const onClick: (staff: IStaff) => IMenuAction['onClick'] = (staff) => () => {
    setConfirmationModalProps({
      open: true,
      title: t('staff.changeOwnership'),
      children: ModalContent(staff),
      cancel: {
        title: t('app.cancel'),
        action: () => {
          setConfirmationModalProps((prev) => ({
            ...prev,
            open: false,
          }));
        },
      },
      confirm: {
        title: t('app.confirm'),
        disabled: false,
        isLoading: false,
        action: () => {
          const { providerId, userId } = staff;
          if (providerId && userId) {
            updateProvider(providerId, { service_provider: { ownerId: userId } });
            setConfirmationModalProps((prev) => ({
              ...prev,
              open: false,
            }));
            setTimeout(
              () => queryClient.invalidateQueries({ queryKey: [REQUEST_ACTIONS.STAFF_LIST] }),
              1000,
            );
          }
        },
      },
    });
  };

  return (item: IStaff) => ({
    name: t('staff.makeOwner'),
    onClick: onClick(item),
  });
};
